// Needs a human's Work it (Task B4, spec §10.4): one item with its record alongside, so the team never decides blind.
// The decision's actions stay with the page (onDecide, onReaddress, onLeave); the panel closes once one succeeds.
import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api.js";
import { followLink } from "../../lib/router.js";
import { fmtWaiting } from "../../lib/when.js";
import { fmtWhen } from "./ui.jsx";
import { KIND } from "./RecordPage.jsx";

const DELIVERY = { sent: "ok", queued: "mid", failed: "bad", bounced: "bad" };
const names = (list) => list.length < 2 ? list.join("") : `${list.slice(0, -1).join(", ")} and ${list.at(-1)}`;

export default function WorkItPanel({ item: r, isAdmin, error: failed, onClose, onDecide, onReaddress, onLeave }) {
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  // One decision at a time: a double click mustn't send a second request.
  const act = (fn) => async () => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const close = useRef(null);
  useEffect(() => {
    if (!r.invitationId) return;
    api(`/backoffice/invitations/${r.invitationId}/record`).then(setRec, (err) => setError(err.message));
  }, [r.invitationId]);
  useEffect(() => {
    const before = document.activeElement;
    close.current.focus();
    // Escape belongs to the confirm dialog while one is open over the panel.
    const onKey = (e) => { if (e.key === "Escape" && !document.querySelector(".dlg")) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); before?.focus?.(); };
  }, []);

  const cancel = r.type === "cancellation";
  const inv = rec?.invitation, reg = rec?.registration;
  const facts = !rec ? [] : reg
    ? [["Registrant", `${inv.name} · ${KIND[inv.kind] ?? inv.kind}`], ["Registered", `${fmtWhen(reg.createdAt)} · ${reg.travelMethod === "B" ? "flying" : "driving"}`],
       ["Email", inv.email], ["Registrant's mobile", rec.registrantPhone || "–"]]
    : [["Invited", `${inv.name} · ${KIND[inv.kind] ?? inv.kind}`], ["Registered", "Not yet"], ["Travelers allowed", inv.travellersAllowed], ["Codes", "None yet"]];
  const staying = rec?.travellers.filter(t => t.code === r.code && t.name !== r.who).map(t => t.name) ?? [];

  return (
    <div className="bo-drawer-backdrop" onClick={onClose}>
      <aside className="bo-drawer workit" onClick={e => e.stopPropagation()} aria-label="Work it">
        <div className="bo-drawer-head"><h2>Work it</h2><button className="bo-link" ref={close} onClick={onClose}>Close</button></div>
        {cancel ? <span className="bo-state bad">Can't travel</span> : <span className="bo-state mid">Bounced</span>}
        <div className="wi-name">{r.who}</div>
        <div className="wi-what">
          {cancel ? <span className="q">{r.detail ? `“${r.detail}”` : "No reason given"}</span>
            : <span className="q">The invitation to <span className="mono">{r.email}</span> couldn't be delivered.</span>}
          <div className="wi-meta">{cancel ? `Asked on ${r.registrant}'s registration, ` : "Bounced "}{fmtWhen(r.at)} · waiting {fmtWaiting(r.at)}
            {cancel && r.travelMethod === "B" ? " · flying, so the flight can't be refunded or renamed" : ""}</div>
        </div>

        {r.invitationId && <div className="wi-h">The record <a className="bo-link" href={`/admin/record/${r.invitationId}`} onClick={followLink(`/admin/record/${r.invitationId}`)}>Open the full record →</a></div>}
        {error && <div className="bo-notice bad">{error}</div>}
        {r.invitationId && !rec && !error && <div className="muted">Loading…</div>}
        {rec && <>
          <dl className="wi-facts">{facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
          {reg && <>
            <div className="wi-h">Who's on it</div>
            <div className="bo-table wi-people">
              {rec.travellers.map((t, i) => (
                <div className="bo-tr" key={i}><div><b>{t.name}</b>{t.registrant && <span className="tag">registrant</span>}
                  {cancel && t.name === r.who && t.code === r.code && <span className="tag this">this request</span>}</div><div className="mono">{t.code}</div></div>))}
              {!rec.travellers.length && <div className="bo-empty">No one is left on this registration.</div>}
            </div>
          </>}
          <div className="wi-h">Latest emails</div>
          <div className="bo-table wi-log">
            {rec.deliveries.slice(0, 3).map((m, i) => (
              <div className="bo-tr" key={i}><div className="muted">{fmtWhen(m.at)}</div><div>{m.subject}{m.error && <div className="err">{m.error}</div>}</div>
                <div><span className={`bo-state ${DELIVERY[m.status] ?? ""}`}>{m.status[0].toUpperCase() + m.status.slice(1)}</span></div></div>))}
            {!rec.deliveries.length && <div className="bo-empty">No emails yet.</div>}
          </div>
        </>}

        <div className="wi-decision">
          <div className="wi-h">Your decision</div>
          {failed && <div className="bo-notice bad" role="alert">{failed}</div>}
          {cancel && r.isRegistrant && r.othersUnbooked > 0 && <p className="wi-warn">{r.who} is the registrant, and {r.othersUnbooked === 1 ? "1 other person on this registration isn't" : `${r.othersUnbooked} other people on this registration aren't`} booked yet. If you approve, {r.who} still books for the others{r.registrantPhone ? ` (mobile ${r.registrantPhone})` : ""}.</p>}
          {!isAdmin && <p className="muted">Only an admin can decide this.</p>}
          {isAdmin && cancel && <>
            <p><b>Approve:</b> {r.who} comes off {r.code ?? "their code"}.{rec && r.code ? ` ${staying.length ? `${names(staying)} ${staying.length === 1 ? "stays" : "stay"} on it.` : "No one is left on it."}` : ""}</p>
            <p><b>Decline:</b> nothing changes. {r.who} stays registered.</p>
            <p className="muted">Each asks you to confirm first, with room for a note.</p>
            <div className="bo-drawer-actions"><button className="bo-btn primary" disabled={busy} onClick={act(() => onDecide("approved"))}>Approve</button><button className="bo-btn" disabled={busy} onClick={act(() => onDecide("declined"))}>Decline</button></div>
          </>}
          {isAdmin && !cancel && <>
            <p>Fix the address and the invitation goes again:</p>
            <div className="wi-fix">
              <input className="bo-num" type="email" aria-label="Working address" placeholder="Working address" value={email} onChange={e => setEmail(e.target.value)} />
              <button className="bo-btn primary" onClick={act(() => onReaddress(email.trim()))} disabled={busy || !email.includes("@")}>Save and send</button>
            </div>
            <p><button className="bo-link" disabled={busy} onClick={act(onLeave)}>Leave it</button> <span className="muted">withdraws this invitation; you can invite them again later.</span></p>
          </>}
        </div>
      </aside>
    </div>
  );
}
